import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, IconButton, InputAdornment, Link, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import AuthLayout from '../components/AuthLayout';
import OAuthProviderButtons from '../components/OAuthProviderButtons';
import OtpInput from '../components/OtpInput';
import { useSession } from '../viewmodels/SessionViewModel';
import { useSignInViewModel } from '../viewmodels/AuthViewModels';
import { errorText } from '../utils/format';

const TERMS_URL = 'https://orkaapps.com/links/terms';
const PRIVACY_URL = 'https://orkaapps.com/links/privacy';

function AuthTermsLine() {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center', lineHeight: 1.6 }}>
      By continuing, you agree to our
      {' '}
      <Link href={TERMS_URL} target="_blank" rel="noopener noreferrer">Terms of Service</Link>
      {' '}
      and
      {' '}
      <Link href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
      .
    </Typography>
  );
}

export default function SignInView() {
  const vm = useSignInViewModel();
  const { loadError, setSignedIn } = useSession();
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    document.title = 'Sign in · OrkaCrew';
  }, []);

  const submitPassword = (e) => {
    e.preventDefault();
    vm.signInWithPassword({ loginId, password });
  };
  const submitOtp = (e) => {
    e.preventDefault();
    if (vm.otpStep === 'request') vm.sendOtp({ loginId });
    else vm.verifyOtp({ loginId, code });
  };

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Use your Orka account. It is the same sign-in as your store."
      footer={(
        <>
          New to OrkaCrew?
          {' '}
          <Link component={RouterLink} to="/signup">Create account</Link>
        </>
      )}
    >
      {loadError && (
        <Alert severity="warning" sx={{ mb: 2 }}>{errorText(loadError)}</Alert>
      )}
      <Tabs value={vm.mode} onChange={(_, v) => { vm.changeMode(v); setCode(''); }} variant="fullWidth" sx={{ mb: 2 }}>
        <Tab value="password" label="Password" />
        <Tab value="otp" label="OTP" />
      </Tabs>

      {vm.error && <Alert severity="error" sx={{ mb: 2 }}>{vm.error}</Alert>}
      {vm.notice && !vm.error && <Alert severity="info" sx={{ mb: 2 }}>{vm.notice}</Alert>}

      {vm.mode === 'password' ? (
        <Box component="form" onSubmit={submitPassword} noValidate>
          <TextField
            label="Email or mobile"
            value={loginId}
            onChange={(e) => setLoginId(e.target.value)}
            fullWidth
            margin="normal"
            autoComplete="username"
            autoFocus
          />
          <TextField
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            margin="normal"
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ textAlign: 'right', mt: 0.5 }}>
            <Link component={RouterLink} to="/forgot-password" variant="body2">Forgot password?</Link>
          </Box>
          <Button type="submit" variant="contained" fullWidth size="large" disabled={vm.busy} sx={{ mt: 2 }}>
            {vm.busy ? <CircularProgress size={22} color="inherit" /> : 'Sign in'}
          </Button>
        </Box>
      ) : (
        <Box component="form" onSubmit={submitOtp} noValidate>
          <TextField
            label="Email or mobile"
            value={loginId}
            onChange={(e) => setLoginId(e.target.value)}
            fullWidth
            margin="normal"
            disabled={vm.otpStep === 'verify'}
            autoFocus
          />
          {vm.otpStep === 'verify' && (
            <>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                Enter the code we sent you
              </Typography>
              <OtpInput value={code} onChange={setCode} disabled={vm.busy} />
            </>
          )}
          <Button type="submit" variant="contained" fullWidth size="large" disabled={vm.busy} sx={{ mt: 2 }}>
            {vm.busy ? <CircularProgress size={22} color="inherit" /> : (vm.otpStep === 'request' ? 'Send code' : 'Verify and sign in')}
          </Button>
          {vm.otpStep === 'verify' && (
            <Button fullWidth sx={{ mt: 1 }} onClick={() => { vm.changeMode('otp'); setCode(''); }}>Use a different number</Button>
          )}
        </Box>
      )}
      {vm.mode === 'password' && (
        <OAuthProviderButtons
          onAuthenticated={setSignedIn}
          onError={vm.reportError}
        />
      )}
      <AuthTermsLine />
    </AuthLayout>
  );
}
